import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  StatusItemIssueSeverity,
  StatusItemIssueType,
} from '@/apps/health_check/types/enums';
import { localStorageKeys } from '@/utils/tokens';
import { LOCAL_STORAGE_FILTERS_AND_SORTING } from '@/utils/configs/healthCheckConfig';
import { saveFilterToLocalStorage } from '@/utils/localStorage';

export interface StatusItemsFiltersState {
  contracts: string[][];
  flattenContracts: string[][];
  types: StatusItemIssueType[];
  severity: StatusItemIssueSeverity[];
  vehicleIds: string;
  depots: any[];
  blockIds: string;
  faultAlarms: string[][];
  flattenFaultAlarms: string[][];
  showMuted: boolean;
  showHealthy: boolean;
}

export const defaultStatusItemsFilters: StatusItemsFiltersState = {
  contracts: [],
  flattenContracts: [],
  types: [],
  depots: [],
  severity: [],
  vehicleIds: '',
  blockIds: '',
  faultAlarms: [],
  flattenFaultAlarms: [],
  showMuted: false,
  showHealthy: false,
};

const initialState: StatusItemsFiltersState = {
  ...defaultStatusItemsFilters,
  ...JSON.parse(
    localStorage.getItem(localStorageKeys.statusItemsFilters) || '{}'
  ),
};

const statusItemsFiltersSlice = createSlice({
  name: 'statusItemsFilters',
  initialState,
  reducers: {
    setStatusItemsFilters(
      state,
      action: PayloadAction<Partial<StatusItemsFiltersState>>
    ) {
      const updatedState = { ...state, ...action.payload };
      saveFilterToLocalStorage(
        localStorageKeys.statusItemsFilters,
        updatedState,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
      return updatedState;
    },
    resetStatusItemsFilters(state) {
      saveFilterToLocalStorage(
        localStorageKeys.statusItemsFilters,
        {
          ...defaultStatusItemsFilters,
          contracts: state.contracts || [],
          flattenContracts: state.flattenContracts || [],
          showMuted: state.showMuted,
          showHealthy: state.showHealthy,
        },
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
      return {
        ...defaultStatusItemsFilters,
        contracts: state.contracts || [],
        flattenContracts: state.flattenContracts || [],
        showMuted: state.showMuted,
        showHealthy: state.showHealthy,
      };
    },
    resetAllStatusItemsFilters() {
      saveFilterToLocalStorage(
        localStorageKeys.statusItemsFilters,
        defaultStatusItemsFilters,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
      return defaultStatusItemsFilters;
    },
    initializeFiltersFromStorage(
      state,
      action: PayloadAction<{ userContracts: number[] }>
    ) {
      const userContracts = action.payload.userContracts || [];
      const isNumericId = (val: string) => /^\d+$/.test(val);

      const validContracts = (state.contracts || []).filter(
        (contractPath: string[]) => {
          if (!contractPath || contractPath.length === 0) {
            return false;
          }
          const leaf = contractPath[contractPath.length - 1];
          if (leaf && isNumericId(leaf)) {
            return userContracts.includes(Number(leaf));
          }
          return (state.flattenContracts || []).some((flatPath: string[]) => {
            if (
              flatPath.length >= contractPath.length &&
              flatPath
                .slice(0, contractPath.length)
                .every((v, i) => v === contractPath[i])
            ) {
              const flatLeaf = flatPath[flatPath.length - 1];
              if (!flatLeaf) {
                return false;
              }
              return (
                isNumericId(flatLeaf) &&
                userContracts.includes(Number(flatLeaf))
              );
            }
            return false;
          });
        }
      );

      const validFlattenContracts = (state.flattenContracts || []).filter(
        (contractPath: string[]) => {
          if (!contractPath || contractPath.length === 0) {
            return false;
          }
          const leaf = contractPath[contractPath.length - 1];
          if (!leaf) {
            return false;
          }
          return isNumericId(leaf) && userContracts.includes(Number(leaf));
        }
      );

      const validatedState = {
        ...state,
        contracts: validContracts,
        flattenContracts: validFlattenContracts,
      };

      saveFilterToLocalStorage(
        localStorageKeys.statusItemsFilters,
        validatedState,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
      return validatedState;
    },
  },
});

export const {
  setStatusItemsFilters,
  resetStatusItemsFilters,
  resetAllStatusItemsFilters,
  initializeFiltersFromStorage,
} = statusItemsFiltersSlice.actions;

export default statusItemsFiltersSlice.reducer;
